import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useEffect, useRef, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useForm, Controller } from "react-hook-form";
import { ICartDelete, useCartDeleteAPI } from "@/app/hooks/api_hooks/Cart/useCartDeleteAPI";
import { useCartListAPI } from "@/app/hooks/api_hooks/Cart/useCartListAPI";
import { useParams } from "react-router";
import RadioGroupComponent from "@/app/components/ui/RadioGroup";

const CartDeleteModalComponent = ({ open, setOpen, cart_id }) => {
  const cancelButtonRef = useRef(null);
  const params = useParams();
  const [isOtherSelected, setIsOtherSelected] = useState(false);
  const [loader, setLoader] = useState(false);
  const { execute: deleteCart } = useCartDeleteAPI();
  const { execute: fetchCartList } = useCartListAPI();

  const formHook = useForm<any>({
    mode: "onChange",
    defaultValues: {
      billingStatus: "Items_already_billed",
      otherReason: null,
    },
  });

  const onSubmit = (data) => {
    const constructedBody: ICartDelete = {
      cart_id: cart_id,
      reason: isOtherSelected ? data.otherReason : data.billingStatus?.split("_")?.join(" "),
    };
    setLoader(true);
    deleteCart(constructedBody).then(({ status }) => {
      if (status) {
        setOpen(false);
        const constructedBodyForList = {
          cart_status: ["Unbilled"],
          start_date: null,
          end_date: null,
        };
        fetchCartList(constructedBodyForList, params.user_id);
        setLoader(false);
        setTimeout(() => {
          formHook.reset();
        }, 2000);
      } else {
        setLoader(false);
      }
    });
  };

  useEffect(() => {
    const billing = formHook.watch("billingStatus");
    if (billing === "Other") {
      setIsOtherSelected(true);
    } else {
      setIsOtherSelected(false);
    }
  }, [formHook.watch("billingStatus")]);

  return (
    <div>
      <Transition.Root show={open} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-50 overflow-y-auto"
          initialFocus={cancelButtonRef}
          onClose={() => setOpen(false)}
        >
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" />
            </Transition.Child>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
              &#8203;
            </span>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <form
                onSubmit={formHook.handleSubmit(onSubmit)}
                className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:w-full sm:max-w-2xl"
              >
                {loader ? (
                  <TableSkeleton />
                ) : (
                  <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                    <div className="flex justify-between items-center mt-2">
                      <h2 className="text-sm font-medium">Are you sure you want to delete this cart?</h2>

                      <span
                        onClick={() => {
                          setOpen(false);
                          formHook.reset();
                        }}
                      >
                        <XMarkIcon className="h-4 w-4 cursor-pointer" />
                      </span>
                    </div>
                    <p className="text-[#808DA0] text-xs mt-1">Please provide the reason</p>
                    <div className="flex flex-col py-8 gap-4">
                      <RadioGroupComponent
                        alignment="vertical"
                        errorMessages={[]}
                        fieldError={formHook.formState.errors.billingStatus}
                        register={formHook.register("billingStatus")}
                        groupOptions={[
                          { id: "Items_already_billed", title: "Items already billed" },
                          { id: "Created_by_mistake", title: "Created by mistake" },
                          { id: "Other", title: "Other" },
                        ]}
                        defaultSelectedId={formHook.watch("billingStatus")}
                        onRadioChange={(optionId: string) => {
                          formHook.setValue("billingStatus", optionId as any);
                        }}
                        radioOptionClassName="text-sm"
                      />
                      {isOtherSelected && (
                        <Controller
                          name="otherReason"
                          control={formHook.control}
                          rules={{
                            required: "Please provide a reason.",
                            validate: (value) => value.trim() !== "" || "Reason cannot be blank.",
                          }}
                          render={({ field }) => (
                            <textarea
                              {...field}
                              rows={4}
                              placeholder="Enter your reason here"
                              className="w-full p-2 border border-gray-300 rounded-xl text-sm"
                            />
                          )}
                        />
                      )}
                      {formHook.formState.errors.otherReason && isOtherSelected && (
                        <p className="text-red-500 text-[10px] -mt-2">
                          {formHook.formState.errors.otherReason.message as any}
                        </p>
                      )}
                    </div>
                    <div className="flex justify-end gap-4 mt-4">
                      <button
                        type="button"
                        onClick={() => {
                          setOpen(false);
                          formHook.reset();
                        }}
                        className="rounded-md bg-[#72767f1a] px-4 py-2 text-sm text-[#000000] font-medium"
                      >
                        Cancel
                      </button>
                      <button type="submit" className="rounded-md bg-[#FF0303] font-bold px-4 py-2 text-sm text-white">
                        Yes,Delete
                      </button>
                    </div>
                  </div>
                )}
              </form>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
};

export default CartDeleteModalComponent;

const TableSkeleton = () => {
  return (
    <div className="bg-white mt-8 h-[300px] overflow-scroll animate-pulse">
      <table className="rounded-md divide-y divide-gray-200 min-w-full">
        <thead className="top-0 sticky bg-[#F7F7F7] shadow-sm rounded-md">
          <tr>
            {[...Array(3)].map((_, index) => (
              <th
                key={index}
                className="px-3 py-3 text-left text-xs font-medium text-gray-300 capitalize tracking-wider"
              >
                <div className="h-3 bg-gray-200 rounded w-3/4"></div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {[...Array(3)].map((_, rowIndex) => (
            <tr key={rowIndex} className="border-b">
              {[...Array(6)].map((_, colIndex) => (
                <td key={colIndex} className="px-3 py-4 whitespace-nowrap text-xs font-normal text-gray-300">
                  <div className="h-3 bg-gray-200 rounded w-full"></div>
                </td>
              ))}
              <td className="px-3 py-4 whitespace-nowrap text-xs font-normal text-gray-300">
                <div className="h-6 bg-gray-200 rounded w-1/2"></div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
